import React, { useState, useEffect } from 'react';
import { addCategory, getCategories, deleteCategory, updateCategory } from '../../services/firestoreService';
import PageHead from '../../components/common/PageHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';

const Categories = () => {
  const [categoryName, setCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      const fetchedCategories = await getCategories();
      setCategories(fetchedCategories);
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      setError('Category name cannot be empty');
      return;
    }

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryName);
        setCategories((prev) =>
          prev.map((cat) => (cat.id === editingCategory.id ? { ...cat, name: categoryName } : cat))
        );
        setEditingCategory(null);
      } else {
        await addCategory(categoryName);
        setCategories((prev) => [...prev, { name: categoryName }]);
      }
      setCategoryName('');
      setError('');
    } catch {
      setError('Failed to add or update category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await deleteCategory(categoryId);
      setCategories((prev) => prev.filter((c) => c.id !== categoryId));
    } catch {
      setError('Failed to delete category');
    }
  };

  return (
    <>
      <PageHead name="Manage Categories" />
      <div className="container mx-auto p-6 max-w-4xl">
        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded p-6 border border-gray-300 mb-6">
          <label className="block mb-2 font-semibold text-gray-700">Category Name</label>
          <input
            type="text"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            placeholder="Enter category name"
            className="w-full p-3 border border-gray-600 rounded focus:outline-none focus:ring focus:ring-primary"
          />
          {error && <p className="text-red-500 mt-2">{error}</p>}
          <div className="mt-4">
            <button
              type="submit"
              className="bg-primary text-white px-6 py-2 rounded hover:bg-secondary transition"
            >
              {editingCategory ? 'Update Category' : 'Add Category'}
            </button>
          </div>
        </form>

        <h2 className="text-xl font-semibold mb-3">Existing Categories</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-100 table-auto border border-gray-600">
            <thead className="bg-gray-200 text-gray-700 text-lg">
              <tr>
                <th className="border border-gray-600 px-4 py-2 w-2/3">Category Name</th>
                <th className="border border-gray-600 px-4 py-2 w-1/3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length > 0 ? (
                categories.map((category, idx) => (
                  <tr key={category.id || idx} className="border-b border-gray-600 hover:bg-gray-100 transition">
                    <td className="h-16 px-4 border border-gray-600">{category.name}</td>
                    <td className="h-16 px-4 border border-gray-600">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => handleEdit(category)}
                          className="p-2 text-primary rounded hover:bg-primary hover:text-white"
                          title="Edit Category"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button
                          onClick={() => handleDelete(category.id)}
                          className="p-2 text-red-500 rounded hover:bg-red-500 hover:text-white"
                          title="Delete Category"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="text-center py-4">No categories found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Categories;
